import React, { useRef, useState } from 'react';
import { Box, IconButton, useTheme } from '@mui/material';
import { Rnd } from 'react-rnd';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { ITables, TABLE_STATUS } from '../../all-types';
import { RestaurantTable } from '../../components';
import { TableRenderer } from './table-renderer';

interface DraggableTableProps {
    table: ITables;
    onUpdatePosition: (table: ITables, x: number, y: number, w: number, h: number, rotation: number) => void;
}

export const DraggableTable: React.FC<DraggableTableProps> = ({
    table,
    onUpdatePosition
}) => {
    const theme = useTheme();
    const boxRef = useRef<HTMLDivElement>(null);
    const [rotation, setRotation] = useState(table.rotation || 0);
    const { width, height, xAxis, yAxis } = table;

    const rotate = (angle: number) => {
        const newRotation = (rotation + angle + 360) % 360;
        setRotation(newRotation);
        onUpdatePosition(table, xAxis, yAxis, width, height, newRotation);
    };

    return (
        <Rnd
            bounds="parent"
            default={{
                x: xAxis || 0,
                y: yAxis || 0,
                width: width || 200,
                height: height || 200
            }}
            style={{
                zIndex: 10,
            }}
            position={{ x: xAxis || 0, y: yAxis || 0 }}
            onDragStop={(e, d) => onUpdatePosition(table, d.x, d.y, width, height, rotation)}
            onResizeStop={(e, direction, ref, delta, position) => {
                const w = parseInt(ref.style.width, 10);
                const h = parseInt(ref.style.height, 10);
                onUpdatePosition(table, position.x, position.y, w, h, rotation);
            }}
            enableResizing={{
                top: true,
                right: true,
                bottom: true,
                left: true,
                topRight: true,
                bottomRight: true,
                bottomLeft: true,
                topLeft: true
            }}
            resizeHandleStyles={{
                top: { height: '30px', cursor: 'ns-resize' },
                right: { width: '30px', cursor: 'ew-resize' },
                bottom: { height: '30px', cursor: 'ns-resize' },
                left: { width: '30px', cursor: 'ew-resize' },
                topRight: { width: '30px', height: '30px', cursor: 'nesw-resize' },
                bottomRight: { width: '30px', height: '30px', cursor: 'nwse-resize' },
                bottomLeft: { width: '30px', height: '30px', cursor: 'nesw-resize' },
                topLeft: { width: '30px', height: '30px', cursor: 'nwse-resize' }
            }}
        >
            <Box
                ref={boxRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    bgcolor: table.billSession === null ? 'green' : 'red',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: `rotate(${rotation}deg)`,
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'move',
                    '&:hover .rotate-btn': { opacity: 1 }
                }}
            >
                {table.tableType === 'DEFAULT' ? (
                    <RestaurantTable isFree={true} table={table} />
                ) : (
                    <TableRenderer {...table} />
                )}

                {/* Rotate Left */}
                <IconButton
                    className="rotate-btn"
                    size="large"
                    onClick={() => rotate(-15)}
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: 'calc(50% - 40px)',
                        bgcolor: theme.palette.background.default,
                        boxShadow: 2,
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        '&:hover': { bgcolor: 'lightgray' }
                    }}
                >
                    <RotateLeftIcon fontSize="medium" />
                </IconButton>

                {/* Rotate Right */}
                <IconButton
                    className="rotate-btn"
                    size="large"
                    onClick={() => rotate(15)}
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: 'calc(50% - 40px)',
                        bgcolor: theme.palette.background.default,
                        boxShadow: 2,
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        '&:hover': { bgcolor: 'lightgray' }
                    }}
                >
                    <RotateRightIcon fontSize="medium" />
                </IconButton>
            </Box>
        </Rnd>
    );
};
